if (!customElements.get('collection-search-grid')) {
  const ICON_PREV = '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 11L1 6L6 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ICON_NEXT = '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 11L6 6L1 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  class CollectionSearchGrid extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.form = this.querySelector('[data-search-form]');
      this.list = this.querySelector('[data-search-list]');
      this.items = Array.from(this.querySelectorAll('[data-search-item]'));
      this.emptyState = this.querySelector('[data-search-empty]');
      this.paginationContainer = this.querySelector('[data-search-pagination]');
      this.perPage = this.getPerPage();
      this.currentPage = 1;

      if (this.items.length === 0) return;

      if (this.input) {
        this.onInputHandler = this.debounce(this.onInput.bind(this), 150);
        this.input.addEventListener('input', this.onInputHandler);
      }

      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
        });
      }

      if (this.paginationContainer) {
        this.onPaginationClickHandler = this.onPaginationClick.bind(this);
        this.paginationContainer.addEventListener('click', this.onPaginationClickHandler);
      }

      this.onResizeHandler = this.debounce(this.onResize.bind(this), 150);
      window.addEventListener('resize', this.onResizeHandler);

      this.render();
    }

    disconnectedCallback() {
      if (this.input) {
        this.input.removeEventListener('input', this.onInputHandler);
      }
      if (this.paginationContainer) {
        this.paginationContainer.removeEventListener('click', this.onPaginationClickHandler);
      }
      if (this.onResizeHandler) {
        window.removeEventListener('resize', this.onResizeHandler);
      }
    }

    getPerPage() {
      const mobileValue = parseInt(this.dataset.perPageMobile, 10);
      const desktopValue = parseInt(this.dataset.perPage, 10);
      if (mobileValue && window.matchMedia('(max-width: 749px)').matches) {
        return mobileValue;
      }
      return desktopValue || this.items.length || 1;
    }

    onResize() {
      const newPerPage = this.getPerPage();
      if (newPerPage === this.perPage) return;
      this.perPage = newPerPage;
      this.currentPage = 1;
      this.render();
    }

    debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    getMatchingItems() {
      const query = this.input ? this.input.value.trim().toLowerCase() : '';
      if (!query) return this.items;
      return this.items.filter((item) =>
        item.dataset.searchTitle.indexOf(query) !== -1 ||
        item.dataset.searchSku.indexOf(query) !== -1
      );
    }

    onInput() {
      this.currentPage = 1;
      this.render();
    }

    onPaginationClick(event) {
      const link = event.target.closest('[data-page]');
      if (!link) return;
      event.preventDefault();
      const page = parseInt(link.dataset.page, 10);
      if (!page || page === this.currentPage) return;
      this.currentPage = page;
      this.render();
    }

    render() {
      const matches = this.getMatchingItems();
      const totalPages = Math.max(1, Math.ceil(matches.length / this.perPage));
      if (this.currentPage > totalPages) this.currentPage = totalPages;

      const start = (this.currentPage - 1) * this.perPage;
      const visible = new Set(matches.slice(start, start + this.perPage));

      this.items.forEach((item) => {
        item.classList.toggle('hidden', !visible.has(item));
      });

      if (this.emptyState) {
        this.emptyState.classList.toggle('hidden', matches.length !== 0);
      }

      this.renderPagination(totalPages);
    }

    getPageNumbers(current, total) {
      const delta = 3;
      const range = [];
      const rangeWithDots = [];
      let last;

      for (let page = 1; page <= total; page += 1) {
        if (page === 1 || page === total || (page >= current - delta && page <= current + delta)) {
          range.push(page);
        }
      }

      range.forEach((page) => {
        if (last !== undefined) {
          if (page - last === 2) {
            rangeWithDots.push(last + 1);
          } else if (page - last > 2) {
            rangeWithDots.push('...');
          }
        }
        rangeWithDots.push(page);
        last = page;
      });

      return rangeWithDots;
    }

    renderPagination(totalPages) {
      if (!this.paginationContainer) return;

      if (totalPages <= 1) {
        this.paginationContainer.innerHTML = '';
        this.paginationContainer.classList.add('hidden');
        return;
      }

      const pageLabel = (number) => (this.dataset.labelPage || 'Page PAGE_NUMBER').replace('PAGE_NUMBER', number);
      const items = [];

      if (this.currentPage > 1) {
        items.push(`<li><a href="#" data-page="${this.currentPage - 1}" class="pagination__item pagination__item--prev pagination__item-arrow por tap-area-icon link flex justify-center align-center motion-reduce" aria-label="${this.dataset.labelPrevious || 'Previous page'}">${ICON_PREV}</a></li>`);
      }

      this.getPageNumbers(this.currentPage, totalPages).forEach((page) => {
        if (page === '...') {
          items.push('<li><span class="pagination__item pagination__item--ellipsis por flex justify-center align-center" aria-hidden="true">…</span></li>');
        } else if (page === this.currentPage) {
          items.push(`<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--number por tap-area-icon flex justify-center align-center pagination__item--current" aria-current="page" aria-label="${pageLabel(page)}">${page}</a></li>`);
        } else {
          items.push(`<li><a href="#" data-page="${page}" class="pagination__item pagination__item--number por tap-area-icon flex justify-center align-center" aria-label="${pageLabel(page)}">${page}</a></li>`);
        }
      });

      if (this.currentPage < totalPages) {
        items.push(`<li><a href="#" data-page="${this.currentPage + 1}" class="pagination__item pagination__item--next pagination__item-arrow por tap-area-icon link flex justify-center align-center motion-reduce" aria-label="${this.dataset.labelNext || 'Next page'}">${ICON_NEXT}</a></li>`);
      }

      this.paginationContainer.classList.remove('hidden');
      this.paginationContainer.innerHTML = `
        <div class="pagination-wrapper">
          <nav class="pagination" role="navigation" aria-label="${this.dataset.labelPagination || 'Pagination'}">
            <ul class="pagination__list list-unstyled flex justify-center flex-wrap" role="list">${items.join('')}</ul>
          </nav>
        </div>
      `;
    }
  }

  customElements.define('collection-search-grid', CollectionSearchGrid);
}
